import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Button,
  Modal,
  Space,
  Input,
  Switch,
  Select,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { debounce } from 'lodash';
import { Descendant } from 'slate';
import {
  rewriteNoteContent,
  adjustNoteContent,
  adjustTextContent,
  sensitiveWordCheck,
} from '@/services/xiaohongshu';
import FullScreenSpin from '@/components/FullscreenSpin';
import MarkdownEditor, {
  MarkdownEditorRef,
  slateValueToString,
  stringToSlateValue,
  toggleMark,
} from './markdownEditor';

interface TextEditorProps {
  note: string; // 从父组件传递的图片数组
  visible: boolean; // 控制 Modal 的显示与隐藏
  onCancel: (v: boolean) => void;
  onFinish: (v: string) => void;
  imgs?: string[]; // 新增可选的图片数组参数
}

const { TextArea } = Input;
const { Option } = Select;

const ContentEditor: React.FC<TextEditorProps> = ({
  note,
  visible,
  onCancel,
  onFinish,
  imgs,
}) => {
  const [suggestion, setSuggestion] = useState('');
  const [modifiedContents, setModifiedContents] = useState<string[]>([note]);
  const [slates, setSlates] = useState<any[]>([stringToSlateValue(note)]);
  const [modalHeight, setModalHeight] = useState('auto');
  const [focus, setFocus] = useState<number>(0);
  const [processing, setProcessing] = useState(false);
  const [selectedText, setSelectedText] = useState<string>('');
  const [useImages, setUseImages] = useState(false);

  // 新增敏感词检测相关状态
  const [selectedProfession, setSelectedProfession] = useState<string>('');
  const [sensitiveWords, setSensitiveWords] = useState<any[]>([]);
  const [checkingWords, setCheckingWords] = useState(false);

  // profession选项
  const professionOptions = [
    { label: '美妆', value: '美妆' },
    {
      label: '医疗 (口腔医疗/医疗美容/医疗器械)',
      value: '医疗 (口腔医疗/医疗美容/医疗器械)',
    },
    { label: '健康养生 (保健食品)', value: '健康养生 (保健食品)' },
    { label: '母婴育儿 (婴幼儿奶粉)', value: '母婴育儿 (婴幼儿奶粉)' },
    { label: '教育', value: '教育' },
    { label: '财经 (房地产)', value: '财经 (房地产)' },
  ];

  const editorRef = useRef<MarkdownEditorRef>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);

  const measureHeight = debounce(() => {
    const heightContent =
      contentRef.current?.getBoundingClientRect().height ?? 520;
    const heightAction =
      actionRef.current?.getBoundingClientRect().height ?? 520;
    // 设置 Modal 内容区域的高度（可加 padding 或 margin）
    setModalHeight(`${Math.max(heightContent + 70, heightAction + 52, 520)}px`);
    // console.log(
    //   `heightContent: ${heightContent}, heightAction: ${heightAction}`,
    // );
  }, 150);

  const updateItem = (newValue: Descendant[]) => {
    setSlates((prevContent) => {
      const updatedContent = [...prevContent]; // 创建一个新的数组副本
      updatedContent[focus] = newValue; // 更新指定索引的值
      return updatedContent; // 返回更新后的数组
    });
  };

  const handleTextSelected = (text: string) => {
    setSelectedText(text);
  };

  useEffect(() => {
    if (visible) {
      measureHeight();
    }
    return () => measureHeight.cancel();
  }, [visible, suggestion, contentRef.current, editorRef.current]);

  const handleContentRegenerate = async (action: 'rewrite' | 'adjust') => {
    setProcessing(true);

    const baseData = {
      content: modifiedContents[focus],
      ...(useImages && imgs && imgs.length > 0 && { imgs }), // 根据开关状态决定是否传递图片
    };

    let response: any;

    if (action === 'rewrite') {
      response = await rewriteNoteContent(baseData);
    } else {
      // action === 'adjust'
      if (!suggestion) {
        message.warning('请输入调整建议');
        setProcessing(false);
        return;
      }
      const adjustData = { ...baseData, suggestion };
      response = await adjustNoteContent(adjustData);
    }

    if (!response?.resp?.content?.result) {
      alert('请求失败,请稍后重试');
      setProcessing(false);
      return;
    }
    setModifiedContents((prev) => {
      const newContents = [...prev, response['resp']['content']['result']];
      setSlates((prev) => {
        const newSlates = [
          ...prev,
          stringToSlateValue(response['resp']['content']['result']),
        ];
        setFocus(newContents.length - 1);
        return newSlates;
      });
      return newContents;
    });
    setProcessing(false);
  };

  const handleTextRegenerate = async () => {
    setProcessing(true);
    const response: any = await adjustTextContent({
      content: modifiedContents[focus],
      suggestion,
      paragraph: selectedText,
    });
    if (!response?.resp?.content?.result) {
      alert('请求失败,请稍后重试');
      setProcessing(false);
      return;
    }
    const newText = response.resp.content.result;
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      toggleMark(editor, 'highlighted');
      toggleMark(editor, 'del');
      editorRef.current.applyReplacement(selectedText, newText);
    }
    setProcessing(false);
  };

  const handleFinish = () => {
    const result = slateValueToString(slates[focus]);
    onFinish(result);
    onCancel(false);
  };

  // 检查敏感词
  const checkSensitiveWords = async (text: string) => {
    if (!text) return;
    setCheckingWords(true);
    const response: any = await sensitiveWordCheck({ content: text });
    setCheckingWords(false);
    if (response?.resp?.sensitiveWords) {
      setSensitiveWords(response.resp.sensitiveWords);
    } else {
      setSensitiveWords([]);
    }
  };

  // 高亮敏感词的函数
  const highlightSensitiveWords = (contrabandList: any[]) => {
    if (!editorRef.current) return;

    // 将API返回的敏感词数据转换为编辑器需要的格式
    const sensitiveWordsForHighlight: any[] = [];

    contrabandList.forEach((item: any) => {
      // 如果API提供了位置信息，使用它
      if (item.start_pos !== undefined && item.end_pos !== undefined) {
        sensitiveWordsForHighlight.push({
          word: item.keyword,
          reason: `${item.lexiconType} - ${item.type}${
            item.meaning ? ': ' + item.meaning : ''
          }`,
          start_pos: item.start_pos,
          end_pos: item.end_pos,
        });
      } else {
        // 如果API没有提供位置信息，只传递关键字，让编辑器内部去查找
        sensitiveWordsForHighlight.push({
          word: item.keyword,
          reason: `${item.lexiconType} - ${item.type}${
            item.meaning ? ': ' + item.meaning : ''
          }`,
          // 不传递位置信息，让编辑器内部查找
        });
      }
    });

    // 调用编辑器的高亮方法
    editorRef.current.highlightSensitiveWords(sensitiveWordsForHighlight);
  };

  // 清除敏感词高亮的函数
  const clearSensitiveWords = () => {
    if (!editorRef.current) return;
    editorRef.current.clearSensitiveWords();
  };

  // 敏感词检测函数
  const handleSensitiveWordCheck = async () => {
    setCheckingWords(true);
    try {
      const currentContent = slateValueToString(slates[focus]);
      const result = await sensitiveWordCheck({
        content: currentContent,
        profession: selectedProfession || '', // 如果没有选择行业类型，传空字符串
      });
      console.log(`敏感词检测结果: ${JSON.stringify(result)}`);

      if (result?.resp?.data?.contrabandList) {
        setSensitiveWords(result.resp.data.contrabandList);

        // 高亮敏感词
        if (result.resp.data.contrabandList.length > 0) {
          highlightSensitiveWords(result.resp.data.contrabandList);

          // 显示检测结果
          Modal.info({
            title: '敏感词检测结果',
            width: 600,
            content: (
              <div>
                <p>
                  检测到 {result.resp.data.contrabandList.length} 个敏感词：
                </p>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {result.resp.data.contrabandList.map(
                    (item: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: '8px',
                          padding: '8px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '4px',
                        }}
                      >
                        <div>
                          <Tag color="red">{item.keyword}</Tag>
                          <span style={{ marginLeft: '8px', color: '#666' }}>
                            {item.lexiconType} - {item.type}
                          </span>
                        </div>
                        {item.meaning && (
                          <div
                            style={{
                              marginTop: '4px',
                              fontSize: '12px',
                              color: '#999',
                            }}
                          >
                            {item.meaning}
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            ),
          });
        } else {
          Modal.success({
            title: '检测通过',
            content: '未检测到敏感词，内容符合规范。',
          });
        }
      }
    } catch (error) {
      console.error('敏感词检测失败:', error);
      Modal.error({
        title: '检测失败',
        content: '敏感词检测服务暂时不可用，请稍后重试。',
      });
    } finally {
      setCheckingWords(false);
    }
  };

  return (
    <>
      {processing && <FullScreenSpin />}
      <Modal
        title="正文编辑"
        open={visible}
        footer={null}
        onCancel={() => onCancel(false)}
        width={1000}
        bodyStyle={{
          height: modalHeight,
          maxHeight: 'none',
          overflowY: 'hidden',
        }}
      >
        <Row gutter={16}>
          {/* 左边展示图片 */}
          <Col span={14}>
            <div
              ref={contentRef}
              style={{
                width: '100%',
                maxHeight: '70vh',
                overflowY: 'scroll',
                fontSize: 16,
              }}
            >
              <MarkdownEditor
                ref={editorRef}
                value={slates[focus]}
                onChange={updateItem}
                onTextSelectAdjust={handleTextSelected}
              />
            </div>
          </Col>
          {/* 右边选择区域 */}
          <Col span={10} ref={actionRef}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div
                style={{
                  padding: '20px',
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  position: 'relative',
                }}
              >
                <h3>选中内容</h3>
                {/* 在右上角添加参考图片开关 */}
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    参考图片
                  </span>
                  <Switch
                    size="small"
                    checked={useImages}
                    onChange={setUseImages}
                    disabled={!imgs || imgs.length === 0} // 如果没有图片则禁用开关
                  />
                </div>
                <div>{selectedText}</div>
              </div>

              {/* 新增行业类型选择 */}
              <div>
                <div
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  行业类型 (用于敏感词检测)
                </div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="请选择行业类型"
                  value={selectedProfession}
                  onChange={setSelectedProfession}
                  allowClear
                >
                  {professionOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>

              <div
                style={{
                  width: '100%',
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                <Space>
                  {modifiedContents.map((content, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: '8px',
                        border:
                          focus === idx
                            ? '2px solid #2ba245'
                            : '2px solid #d9d9d9',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: 'black',
                        fontSize: 18,
                      }}
                      onClick={() => {
                        setFocus(idx);
                      }}
                    >
                      {`第${idx}版`}
                    </div>
                  ))}
                </Space>
              </div>
              <TextArea
                autoSize={{ minRows: 6, maxRows: 16 }}
                placeholder="输入对正文更改的建议"
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
              />
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  type="primary"
                  onClick={() =>
                    selectedText
                      ? handleTextRegenerate()
                      : handleContentRegenerate('adjust')
                  }
                  style={{ width: '100%' }}
                >
                  智能改写
                </Button>
                {/* <Button
                  type="primary"
                  onClick={() => handleContentRegenerate('rewrite')}
                  style={{ width: '45%' }}
                >
                  智能改写
                </Button> */}
              </div>

              {/* 新增敏感词检测按钮 */}
              <Button
                type="default"
                onClick={handleSensitiveWordCheck}
                loading={checkingWords}
                style={{
                  width: '100%',
                  borderColor: '#ff7a45',
                  color: '#ff7a45',
                }}
              >
                {checkingWords ? '检测中...' : '敏感词检测'}
              </Button>

              <Button
                type="primary"
                onClick={handleFinish}
                style={{
                  width: '100%',
                  backgroundColor: '#2ba245',
                  borderColor: '#2ba245',
                }}
              >
                确认修改
              </Button>
            </Space>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ContentEditor;
